import { head, last, flatMap, sortBy, orderBy, groupBy, remove } from 'lodash';
import { observable, computed, runInAction, makeObservable } from 'mobx';
import {
    object,
    list,
    update,
    AdditionalPropArgs,
    Clazz,
    ModelSchema,
    PropSchema,
    custom,
    SKIP,
    createModelSchema
} from 'serializr';
import Context from 'serializr/lib/core/Context';

interface Entity {
    id?: number | null;
}

interface Props {
    [propName: string]: PropSchema;
}

export type RequiredProp<T, K extends keyof T> = T &
    {
        [P in K]-?: NonNullable<T[P]>;
    };

export function entityListSchema<T extends Entity>(Type: Clazz<T>) {
    return createModelSchema<EntityList<T>>(
        EntityList,
        {
            items: custom(
                list(object(Type)).serializer,
                (jsonItems: Entity[], context, _, callback) => {
                    runInAction(() => {
                        const target = context.target as EntityList<T>;
                        const { type } = context.json as EntityList<T>;

                        switch (type) {
                            case 'delete':
                                jsonItems.forEach((entity: Entity) => {
                                    target._map.delete(entity.id);

                                    const ix = target.items.findIndex(
                                        (it: Entity) => it.id === entity.id
                                    );
                                    if (ix > -1) target.items.splice(ix, 1);
                                });
                                callback(null, SKIP);
                                break;
                            case 'update':
                                jsonItems.forEach((entity: Entity) => {
                                    const item = target._map.get(entity.id);
                                    if (item) update(item, entity);
                                });
                                callback(null, SKIP);
                                break;
                            case 'create':
                                list(object(Type)).deserializer(
                                    jsonItems,
                                    (err, newItems?: T[]) => {
                                        if (err) {
                                            callback(err, undefined);
                                        } else {
                                        }
                                        newItems?.forEach((newItem) => {
                                            context.target._map.set(newItem.id, newItem);

                                            const existingElement = target.items.find(
                                                (it: Entity) => it.id === newItem.id
                                            );

                                            if (newItem.id && existingElement) {
                                                target.items.remove(existingElement);
                                            }

                                            target.items.push(newItem);
                                        });
                                        callback(null, SKIP);
                                    },
                                    context
                                );

                                break;
                            case 'load':
                            default:
                                target._map.clear();
                                target.items.clear();

                                list(object(Type)).deserializer(
                                    jsonItems,
                                    (err, items?: T[]) => {
                                        if (err) {
                                            callback(err, undefined);
                                        } else {
                                            items?.forEach((newItem) => {
                                                target._map.set(newItem.id, newItem);
                                                target.items.push(newItem);
                                            });
                                            callback(null, SKIP);
                                        }
                                    },
                                    context
                                );
                                break;
                        }
                    });
                }
            )
        },
        () => new EntityList(Type)
    );
}

export function entityList<T extends Entity>(Type: Clazz<T>, additionalArgs?: AdditionalPropArgs) {
    const modelSchema = entityListSchema(Type);
    return custom(
        object(modelSchema).serializer,
        (
            jsonValue: EntityList | undefined,
            context,
            target: EntityList<T> | undefined | null,
            callback
        ) => {
            if (!jsonValue) {
                return callback(null, jsonValue);
            }
            if (!target) {
                target = modelSchema.factory(context);
            }
            const { deserializer } = modelSchema.props.items as PropSchema;

            deserializer(jsonValue.items, (err) => callback(err, target), {
                ...context,
                target,
                parentContext: context,
                json: jsonValue
            } as Context);
        },
        additionalArgs
    );
}

// EntityList consumers should implement computed list on top of _map
export class EntityList<T extends Entity = any> {
    serializeInfo: any;

    _map = observable.map<number, T>();

    items = observable.array<T>();

    type = 'load';

    forEach: this['items']['forEach'] = this.items.forEach.bind(this.items);
    map: this['items']['map'] = this.items.map.bind(this.items);
    reduce: this['items']['reduce'] = this.items.reduce.bind(this.items);
    slice: this['items']['slice'] = this.items.slice.bind(this.items);
    splice: this['items']['splice'] = this.items.splice.bind(this.items);
    find: this['items']['find'] = this.items.find.bind(this.items);
    some: this['items']['some'] = this.items.some.bind(this.items);
    filter: this['items']['filter'] = this.items.filter.bind(this.items);
    push: this['items']['push'] = this.items.push.bind(this.items);
    unshift: this['items']['unshift'] = this.items.unshift.bind(this.items);

    get = this._map.get.bind(this._map);

    first = () => head(this.items);
    last = () => last(this.items);
    remove = (predicate) => remove(this.items, predicate);
    flatMap = (iteratee: object) => flatMap(this.items, iteratee);
    sortBy = (...iteratees) => sortBy(this.items, ...iteratees);
    orderBy = (iteratees, orders) => orderBy(this.items, iteratees, orders);
    groupBy = (iteratee) => groupBy(this.items, iteratee);
    filterRequired<K extends keyof T>(
        props: K[],
        callbackfn: (value: RequiredProp<T, K>, index: number, array: T[]) => unknown = () => true
    ) {
        return this.items
            .filter((item): item is RequiredProp<T, K> =>
                props.every((prop) => item[prop] !== null && item[prop] !== undefined)
            )
            .filter(callbackfn);
    }

    constructor(Type: Clazz<T>) {
        makeObservable(this, {
            _map: observable,
            items: observable,
            length: computed
        });

        this.serializeInfo = entityListSchema(Type);
    }

    get length() {
        return this.items.length;
    }
}
